import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
    getAiUsageStatus,
    getCopilotProgress,
    getCopilotSession,
    listCopilotSessions,
    sendCopilotMessage,
    subscribeCopilotEvents,
} from '../api/copilot-api';
import { restoreClarification, sanitizeClarification } from '../model/clarification';
import { sanitizeTesting } from '../model/testing';
import { initialState, safeReducer } from '../model/copilot-state';

/** Fired on window when the copilot changed agent configuration, so open pages can refresh. */
export const RESOURCE_CHANGED_EVENT = 'bc-copilot:resource-changed';

// Separate from pages/copilot, so the two copilots keep their own conversations.
const storageKey = scope => `bc-copilot-native:${scope}:session`;
const clarificationKey = (scope, session) => `bc-copilot-native:${scope}:${session}:clarification`;
const pendingCard = (scope, session) => {
    try { return sanitizeClarification(JSON.parse(window.localStorage.getItem(clarificationKey(scope, session)) || 'null')); } catch (_) { return null; }
};
const saveCard = (scope, session, card) => {
    try {
        if (card) window.localStorage.setItem(clarificationKey(scope, session), JSON.stringify(card));
        else window.localStorage.removeItem(clarificationKey(scope, session));
    } catch (_) { /* Storage unavailable. */ }
};
const testingKey = (scope, session) => `bc-copilot-native:${scope}:${session}:testing`;
function storedTesting(scope, session, data) {
    try {
        if (data) window.localStorage.setItem(testingKey(scope, session), JSON.stringify(data));
        else return JSON.parse(window.localStorage.getItem(testingKey(scope, session)) || 'null');
    } catch (_) { /* Optional recovery cache; the test API is the source of truth. */ }
    return null;
}
const HANDOFF_KEY = 'bc-copilot-native:handoff';
// A copilot that is already running (the workspace keeps one per app across
// pages) hears about a new hand-off through this event; storage covers one
// that mounts later.
const HANDOFF_EVENT = 'bc-copilot-native:handoff';

function newSessionId() {
    let random = '';
    try {
        random = window.crypto?.randomUUID?.() || '';
    } catch (_) {
        // insecure context: fall through
    }
    random = random || `${Date.now()}${Math.random()}`;
    return `copilot-${random.replace(/[^A-Za-z0-9_-]/g, '')}`.slice(0, 64);
}

const messageOf = (error, fallback) => (error && typeof error.message === 'string' && error.message) || fallback;

function notifyResourceChanged(detail) {
    try {
        window.dispatchEvent(new CustomEvent(RESOURCE_CHANGED_EVENT, { detail }));
    } catch (error) {
        // a listener on another page must never break the copilot
        console.error('[copilot] resource-changed listener failed', error);
    }
}

function readSession(scope) {
    try {
        return window.localStorage.getItem(storageKey(scope));
    } catch (_) {
        return null;
    }
}

function writeSession(scope, sessionId) {
    try {
        window.localStorage.setItem(storageKey(scope), sessionId);
    } catch (_) {
        // storage unavailable: the session just won't survive a reload
    }
}

/**
 * Remember a message to pre-fill in the copilot of another scope (set before a ROUTE).
 *
 * `newChat`: open it in a fresh conversation instead of the scope's current
 * one, by making a new session the scope's stored session before the copilot
 * mounts. `maxChars`: longer hand-offs (e.g. a fix request from Analytics,
 * with its evidence) fit up to the composer's own limit.
 */
export function stashHandoff(scope, text, { newChat = false, maxChars = 2000 } = {}) {
    if (newChat) writeSession(scope, newSessionId());
    try {
        window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ scope, text: String(text).slice(0, maxChars) }));
    } catch (_) {
        // storage unavailable: the builder just types it again
    }
    try {
        window.dispatchEvent(new CustomEvent(HANDOFF_EVENT, { detail: { scope, newChat } }));
    } catch (_) {
        // no running copilot to tell; the stored hand-off is read when one mounts
    }
}

function takeHandoff(scope) {
    try {
        const stored = JSON.parse(window.sessionStorage.getItem(HANDOFF_KEY) || 'null');
        if (stored && stored.scope === scope && typeof stored.text === 'string') {
            window.sessionStorage.removeItem(HANDOFF_KEY);
            return stored.text;
        }
    } catch (_) {
        // ignore unreadable storage
    }
    return '';
}

/**
 * One copilot conversation for a scope (an appId, or `account:<accountId>`). The subscription is opened while `active` is true
 * (panel open); events that arrive while it is closed are recovered from the transcript
 * when it reopens.
 */
export default function useCopilot(appId, active, { onAction, onResourceFocus, previewSessionId } = {}) {
    const [state, dispatch] = useReducer(safeReducer, initialState);
    const [sessionId, setSessionId] = useState(() => (appId && readSession(appId)) || newSessionId());
    const [connection, setConnection] = useState('idle');
    const [loadingHistory, setLoadingHistory] = useState(false);
    // The session the stored transcript was last loaded for: a message sent before then is overwritten.
    const [historySession, setHistorySession] = useState(null);
    const [sessions, setSessions] = useState([]);
    const subscriptionRef = useRef(null);
    // Settles once this session's subscription is open (or failed): a send made earlier waits for it.
    const subscriptionPending = useRef(Promise.resolve(null));
    const [draft, setDraft] = useState('');
    const handledActions = useRef(new Set());
    const onActionRef = useRef(onAction);
    onActionRef.current = onAction;
    const onResourceFocusRef = useRef(onResourceFocus);
    onResourceFocusRef.current = onResourceFocus;
    const [aiUsage, setAiUsage] = useState(null);

    // The AI usage cap: read when the copilot opens and again after every turn, since the
    // turn that reaches the cap is the one that turns it on.
    useEffect(() => {
        if (!active || state.running) return undefined;
        let cancelled = false;
        getAiUsageStatus().then(status => { if (!cancelled) setAiUsage(status); });
        return () => { cancelled = true; };
    }, [active, state.running]);

    const sessionIdRef = useRef(sessionId);
    sessionIdRef.current = sessionId;

    // Only a hand-off that is there sets the draft: StrictMode runs this twice,
    // and the second (empty) read must not blank what the first one found.
    useEffect(() => {
        if (!appId) return;
        const text = takeHandoff(appId);
        if (text) setDraft(text);
    }, [appId]);

    // A hand-off made while this copilot is already running, e.g. Analytics'
    // "Fix with AI" with the workspace copilot mounted for the app.
    useEffect(() => {
        if (!appId) return undefined;
        const onHandoff = event => {
            if (event.detail?.scope !== appId) return;
            const stored = event.detail.newChat ? readSession(appId) : null;
            if (stored && stored !== sessionIdRef.current) {
                dispatch({ type: 'reset' });
                setSessionId(stored);
            }
            const text = takeHandoff(appId);
            if (text) setDraft(text);
        };
        window.addEventListener(HANDOFF_EVENT, onHandoff);
        return () => window.removeEventListener(HANDOFF_EVENT, onHandoff);
    }, [appId]);

    useEffect(() => {
        if (appId) setSessionId(readSession(appId) || newSessionId());
    }, [appId]);

    useEffect(() => {
        if (!appId || !active || !sessionId) return undefined;
        let cancelled = false;
        const completedTexts = new Map();
        let subscribed;
        const subscription = new Promise(resolve => { subscribed = resolve; });
        subscriptionPending.current = subscription;

        (async () => {
            setLoadingHistory(true);
            let resuming = null;
            try {
                // A turn can outlive the page (website fetches, sub-agents run for minutes):
                // the runtime records its progress, so a reload shows it still working.
                const [{ session, messages }, progress] = await Promise.all([
                    getCopilotSession(appId, sessionId), getCopilotProgress(sessionId),
                ]);
                if (!cancelled) {
                    resuming = progress?.turn?.status === 'running' ? progress : null;
                    const restored = restoreClarification(messages, pendingCard(appId, sessionId));
                    const cached = storedTesting(appId, sessionId);
                    const testing = sanitizeTesting(cached?.testing);
                    if (testing && cached?.text) {
                        const matching = [...restored].reverse().find(row => row.role === 'assistant' && row.content === cached.text);
                        if (matching) matching.testing = testing;
                    }
                    dispatch({ type: 'hydrate', messages: restored, status: resuming ? 'running' : session.status });
                }
            } catch (_) {
                if (!cancelled) dispatch({ type: 'reset' }); // new session: nothing stored yet
            } finally {
                if (!cancelled) {
                    setLoadingHistory(false);
                    setHistorySession(sessionId);
                }
            }
            if (!resuming || cancelled) return;
            // Its steps so far, then the live events that arrived meanwhile; the reply ends it as usual.
            const live = await subscription;
            if (!cancelled) live?.translator?.resumeRun(`turn-${resuming.turn.turnId}`, resuming.events);
        })();

        (async () => {
            try {
                const subscription = await subscribeCopilotEvents(appId, sessionId, {
                    onEvent: event => {
                        if (cancelled) return;
                        if (event?.type === 'clarification.requested') {
                            const card = sanitizeClarification(event.data);
                            if (card) saveCard(appId, sessionId, card);
                        }
                        if (event?.type === 'testing.plan' || event?.type === 'testing.run') {
                            const testing = sanitizeTesting(event.data);
                            if (testing) {
                                const cached = storedTesting(appId, sessionId);
                                storedTesting(appId, sessionId, { testing, copilotRunId: event.runId,
                                    text: completedTexts.get(event.runId) || (cached?.copilotRunId === event.runId ? cached.text : null) });
                            }
                        }
                        if (event?.type === 'run.completed') {
                            const cached = storedTesting(appId, sessionId);
                            // Keep the artifact on its original reply when recovering history.
                            completedTexts.set(event.runId, event.data?.text);
                            if (cached?.copilotRunId === event.runId) storedTesting(appId, sessionId, { ...cached, text: event.data?.text });
                        }
                        // Focus changes the workspace, not the conversation transcript.
                        if (event?.type !== 'resource.focus') dispatch({ type: 'event', event });
                        // A resumed turn's earlier steps already happened: show them, don't redo their effects.
                        if (event?.replay) return;
                        if (event?.type === 'resource.changed') notifyResourceChanged({ appId, ...(event.data || {}) });
                        const handleNavigation = event?.type === 'resource.focus' ? onResourceFocusRef.current
                            : event?.type === 'action' ? onActionRef.current : null;
                        if (event?.data && typeof handleNavigation === 'function') {
                            // Follow each action once, even if the event is redelivered after a reconnect.
                            const identity = event.type === 'resource.focus' && typeof event.data.eventId === 'string'
                                && event.data.eventId ? event.data.eventId : `${event.runId}:${event.seq}`;
                            const key = `${sessionId}:${event.type}:${identity}`;
                            if (!handledActions.current.has(key)) {
                                handledActions.current.add(key);
                                try {
                                    handleNavigation(event.data);
                                } catch (error) {
                                    console.error('[copilot] action failed', error);
                                }
                            }
                        }
                    },
                    onError: error => !cancelled && dispatch({ type: 'send_failed', error: messageOf(error, 'The copilot connection failed.') }),
                    onStatus: status => !cancelled && setConnection(status),
                });
                if (cancelled) subscription.close();
                else subscriptionRef.current = subscription;
                subscribed(cancelled ? null : subscription);
            } catch (error) {
                subscribed(null);
                if (!cancelled) {
                    setConnection('closed');
                    dispatch({ type: 'send_failed', error: messageOf(error, "Couldn't connect to the copilot.") });
                }
            }
        })();

        return () => {
            cancelled = true;
            try {
                subscriptionRef.current?.close();
            } catch (_) {
                // socket already gone
            }
            subscriptionRef.current = null;
        };
    }, [appId, active, sessionId]);

    const dispatchToCopilot = useCallback(async ({ message, approval, display, answer }) => {
        const previousCard = pendingCard(appId, sessionId);
        saveCard(appId, sessionId, null);
        dispatch({ type: 'user_sent', text: display ?? message });
        try {
            // Subscribe before sending, or the first events of the run are missed.
            if (!subscriptionRef.current) await subscriptionPending.current;
            if (subscriptionRef.current) await subscriptionRef.current.ready;
            writeSession(appId, sessionId);
            await sendCopilotMessage(appId, sessionId, { message, approval, previewSessionId, answer }, subscriptionRef.current);
        } catch (error) {
            if (previousCard) saveCard(appId, sessionId, previousCard);
            dispatch({ type: 'send_failed', error: messageOf(error, 'Could not send your message.') });
        }
    }, [appId, sessionId, previewSessionId]);

    /**
     * Send a message. `display` is what the chat shows instead (an onboarding card's answer);
     * `answer` ({slot, value}) answers a playbook card with data (see sendCopilotMessage).
     */
    const send = useCallback((text, display, answer) => {
        const message = typeof text === 'string' ? text.trim() : '';
        if (!message || state.running) return;
        return dispatchToCopilot({
            message,
            ...(typeof display === 'string' && display ? { display } : {}),
            ...(answer && typeof answer.slot === 'string' ? { answer } : {}),
        });
    }, [dispatchToCopilot, state.running]);

    const decide = useCallback((decision, reason) => {
        // A typed rejection reason is shown like any other message the builder sent.
        dispatchToCopilot({ approval: { decision, ...(reason ? { message: reason } : {}) }, display: reason });
    }, [dispatchToCopilot]);

    const newChat = useCallback(() => {
        const id = newSessionId();
        writeSession(appId, id);
        dispatch({ type: 'reset' });
        setSessionId(id);
    }, [appId]);

    const openSession = useCallback(id => {
        if (id === sessionId) return;
        writeSession(appId, id);
        dispatch({ type: 'reset' });
        setSessionId(id);
    }, [appId, sessionId]);

    const clearDraft = useCallback(() => setDraft(''), []);

    /** A document the copilot asked for was uploaded from its card: tell the copilot. */
    const uploaded = useCallback(({ dataSourceId, fileName }) => {
        if (state.running) return;
        dispatchToCopilot({
            message: `I uploaded "${fileName}" as knowledge source ${dataSourceId}. Check that it is ready, then continue.`,
            display: `Uploaded ${fileName}`,
        });
    }, [dispatchToCopilot, state.running]);

    const refreshSessions = useCallback(async () => {
        try {
            const items = await listCopilotSessions(appId);
            setSessions(Array.isArray(items) ? items.filter(item => item && typeof item.sessionId === 'string') : []);
        } catch (_) {
            setSessions([]);
        }
    }, [appId]);

    return {
        ...state,
        // The application being built (null outside one): upload cards register sources there.
        appId: appId && !String(appId).startsWith('account:') ? appId : null,
        sessionId,
        connection,
        loadingHistory,
        historyLoaded: historySession === sessionId,
        sessions,
        // { blocked, message } of the Free plan AI usage cap, or null when unknown.
        aiUsage,
        draft,
        clearDraft,
        send,
        uploaded,
        approve: () => decide('approve'),
        reject: reason => decide('reject', reason),
        newChat,
        openSession,
        refreshSessions,
    };
}
